import axios from "axios";
import { storage } from "../utils";
import { publicEnv } from "../utils/env";

export const api = axios.create({
  baseURL: publicEnv.BUN_PUBLIC_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const logAndRedirect = (reason: string, extra?: unknown) => {
  console.error("[interceptor] REDIRECT REASON:", { reason, extra, time: new Date().toISOString() });
  window.location.href = "/login";
};

api.interceptors.request.use((config) => {
  const tokens = storage.getTokens();
  if (tokens && config.headers) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const tokens = storage.getTokens();

      if (!tokens?.refreshToken) {
        storage.clearTokens();
        logAndRedirect("no_refresh_token");
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${publicEnv.BUN_PUBLIC_API_URL}/api/v1/auth/refresh`,
          { refresh_token: tokens.refreshToken }
        );

        const newTokens = {
          accessToken: data.data.access_token,
          refreshToken: data.data.refresh_token,
          expiredAt: data.data.expired_at,
        };

        storage.setTokens(newTokens);
        api.defaults.headers.common.Authorization = `Bearer ${newTokens.accessToken}`;
        processQueue(null, newTokens.accessToken);

        originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        storage.clearTokens();
        logAndRedirect("refresh_failed", {
          error: (refreshError as any)?.response?.data,
          status: (refreshError as any)?.response?.status,
        });
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const backendMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Неизвестная ошибка";

    const enhancedError = new Error(backendMessage);
    (enhancedError as any).response = error.response;
    return Promise.reject(enhancedError);
  }
);
